import { MarketingContributionPanel } from './analytics/MarketingContributionPanel';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  ClipboardPenLine,
  LayoutDashboard,
  LetterText,
  Loader2,
  Mail,
  Plus,
  Settings,
  Shield,
  Users,
  Workflow,
} from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { EmptyState } from '../../components/EmptyState';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { Select } from '../../components/ui/Select';
import { marketingApi, type MarketingFlowLifecycleStatus, type MarketingSequenceIntakeSummary } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { MarketingAudiencePanel } from './audience/MarketingAudiencePanel';
import { MarketingBroadcastPanel } from './broadcasts/MarketingBroadcastPanel';
import { MarketingSequenceIntakeModal } from './sequence-intake/MarketingSequenceIntakeModal';

export type MarketingEmailView =
  | 'overview'
  | 'messages'
  | 'automations'
  | 'sends'
  | 'recipients'
  | 'analytics'
  | 'settings';

const emailNavigation = [
  { label: 'Przegląd', to: '/marketing/email', icon: LayoutDashboard, end: true },
  { label: 'Wiadomości', to: '/marketing/email/wiadomosci', icon: LetterText, end: false },
  { label: 'Automatyzacje', to: '/marketing/email/automatyzacje', icon: Workflow, end: false },
  { label: 'Wysyłki i kalendarz', to: '/marketing/email/wysylki', icon: CalendarDays, end: false },
  { label: 'Odbiorcy', to: '/marketing/email/odbiorcy', icon: Users, end: false },
  { label: 'Analityka', to: '/marketing/email/analityka', icon: BarChart3, end: false },
] as const;

const viewConfig: Record<MarketingEmailView, {
  title: string;
  subtitle: string;
  emptyTitle: string;
  emptyDescription: string;
  icon: typeof Mail;
}> = {
  overview: {
    title: 'Email - przegląd',
    subtitle: 'Stan domeny, automatyzacji, wysyłek i wyników marketingowych.',
    emptyTitle: 'Email Marketing jest przygotowywany',
    emptyDescription: 'Najpierw skonfigurujemy markę, zgodę odbiorców i domenę wysyłkową. Obecne wiadomości serwisowe pozostają bez zmian.',
    icon: Mail,
  },
  messages: {
    title: 'Wiadomości email',
    subtitle: 'Twórz niezmienne wersje treści w trybie blokowym lub plain text.',
    emptyTitle: 'Brak wiadomości email',
    emptyDescription: 'Utwórz pierwszą wiadomość jako wersjonowany EcomFlow JSON. Render i wysyłka pozostają zablokowane.',
    icon: LetterText,
  },
  automations: {
    title: 'Automatyzacje email',
    subtitle: 'Projektuj flow według celu lub sprawdzonego blueprintu.',
    emptyTitle: 'Brak automatyzacji email',
    emptyDescription: 'Zacznij od wizualnego szkicu flow. Każdy zapis serwerowy utworzy osobną, niezmienną wersję gotową do jawnej publikacji.',
    icon: Workflow,
  },
  sends: {
    title: 'Wysyłki i kalendarz',
    subtitle: 'Planuj jednorazowe wysyłki i kontroluj kolizje komunikacji.',
    emptyTitle: 'Brak zaplanowanych wysyłek',
    emptyDescription: 'Kalendarz pokaże broadcasty, okna sprzedażowe, limity częstotliwości i konflikty ofert.',
    icon: CalendarDays,
  },
  recipients: {
    title: 'Odbiorcy',
    subtitle: 'Buduj wersjonowane segmenty, sprawdzaj dane i zarządzaj bezpieczną allowlistą testową.',
    emptyTitle: 'Brak odbiorców marketingowych',
    emptyDescription: 'Kontakt będzie kwalifikowany do marketingu dopiero po zapisaniu wiarygodnej podstawy i zakresu zgody.',
    icon: Users,
  },
  analytics: {
    title: 'Analityka email',
    subtitle: 'Przychód, zwroty i marża pokrycia z udokumentowanymi kosztami.',
    emptyTitle: 'Brak danych do analizy',
    emptyDescription: 'Metryki pojawią się po pierwszych wysyłkach. Przychód i zysk będą prezentowane oddzielnie.',
    icon: BarChart3,
  },
  settings: {
    title: 'Ustawienia Email',
    subtitle: 'Domeny, provider, limity, zgody i centrum preferencji.',
    emptyTitle: 'Konfiguracja Email nie jest jeszcze aktywna',
    emptyDescription: 'Ustawienia zostaną udostępnione po zamknięciu bramki providera i wdrożeniu bezpiecznego onboardingu domeny.',
    icon: Settings,
  },
};

function EmailNavigation({ showSettings }: { showSettings: boolean }) {
  return (
    <div className="mb-6 flex items-center gap-2 overflow-x-auto rounded-card border border-border-subtle bg-surface-raised/40 p-2">
      <nav aria-label="Widoki Email" className="flex min-w-max items-center gap-1">
        {emailNavigation.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => [
              'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/50',
              isActive
                ? 'border-accent-primary/30 bg-accent-primary/15 text-accent-primary'
                : 'border-transparent text-text-secondary hover:bg-surface-hover/50 hover:text-text-primary',
            ].join(' ')}
          >
            <item.icon className="h-4 w-4" aria-hidden="true" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {showSettings && (
        <NavLink
          to="/marketing/email/ustawienia"
          className={({ isActive }) => [
            'ml-auto inline-flex min-w-max items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/50',
            isActive
              ? 'border-accent-primary/30 bg-accent-primary/15 text-accent-primary'
              : 'border-transparent text-text-secondary hover:bg-surface-hover/50 hover:text-text-primary',
          ].join(' ')}
        >
          <Settings className="h-4 w-4" aria-hidden="true" />
          Ustawienia Email
        </NavLink>
      )}
    </div>
  );
}

const FLOW_STATUS_LABELS: Record<MarketingFlowLifecycleStatus, string> = {
  DRAFT: 'Szkic',
  ACTIVE: 'Aktywna',
  PAUSED: 'Wstrzymana',
  ARCHIVED: 'Zarchiwizowana',
};

interface MarketingApiErrorPayload {
  code?: string;
  error?: string;
}

function describeMarketingLoadError(error: unknown): string {
  if (!isAxiosError<MarketingApiErrorPayload>(error)) {
    return 'Nie udało się pobrać danych z serwera EcomFlow. Spróbuj ponownie.';
  }

  if (error.response?.data?.code === 'DATABASE_SCHEMA_OUT_OF_DATE') {
    return 'Wdrożenie EcomFlow nie zakończyło aktualizacji bazy danych. Spróbuj ponownie po zakończeniu wdrożenia.';
  }
  if (error.response?.status === 403) {
    return 'Twoje konto nie ma uprawnień do odczytu danych Marketingu. Skontaktuj się z administratorem konta.';
  }
  if (error.response?.status === 404) {
    return 'Backend EcomFlow nie udostępnia jeszcze tej funkcji. Sprawdź, czy wdrożenie API zostało zakończone.';
  }
  if (error.response?.status === 503) {
    return 'Backend EcomFlow jest chwilowo niedostępny lub czeka na zakończenie aktualizacji bazy danych.';
  }
  if (!error.response) {
    return 'Nie udało się połączyć z backendem EcomFlow. Sprawdź stan wdrożenia i spróbuj ponownie.';
  }

  return 'Backend EcomFlow zwrócił błąd podczas pobierania danych. Spróbuj ponownie lub sprawdź logi wdrożenia.';
}

function MarketingAutomationsPanel() {
  const navigate = useNavigate();
  const [brandId, setBrandId] = useState('');
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [editingIntake, setEditingIntake] = useState<MarketingSequenceIntakeSummary | null>(null);
  const brandsQuery = useQuery({
    queryKey: ['marketing-brands'],
    queryFn: async () => (await marketingApi.listBrands()).data,
    staleTime: 60_000,
  });
  const brands = brandsQuery.data ?? [];
  const selectedBrandId = brands.some(brand => brand.id === brandId) ? brandId : brands[0]?.id ?? '';
  const flowsQuery = useQuery({
    queryKey: ['marketing-flows', selectedBrandId],
    queryFn: async () => (await marketingApi.listFlows(selectedBrandId)).data,
    enabled: Boolean(selectedBrandId),
  });
  const intakesQuery = useQuery({
    queryKey: ['marketing-sequence-intakes', selectedBrandId],
    queryFn: async () => (await marketingApi.listSequenceIntakes(selectedBrandId)).data,
    enabled: Boolean(selectedBrandId),
  });

  if (brandsQuery.isLoading) {
    return (
      <div className="flex min-h-48 items-center justify-center text-accent-primary" aria-label="Ładowanie automatyzacji">
        <Loader2 className="h-7 w-7 animate-spin" />
      </div>
    );
  }
  if (brandsQuery.isError) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Nie udało się pobrać przestrzeni marki"
        description={describeMarketingLoadError(brandsQuery.error)}
        buttonLabel="Spróbuj ponownie"
        onButtonClick={() => void brandsQuery.refetch()}
      />
    );
  }
  if (!selectedBrandId) {
    return (
      <EmptyState
        icon={Workflow}
        title="Brak przestrzeni marki"
        description="Utwórz sklep lub MarketingBrand, aby rozpocząć projektowanie automatyzacji email."
      />
    );
  }

  const flows = flowsQuery.data ?? [];
  const intakes = intakesQuery.data ?? [];
  const openNewIntake = () => {
    setEditingIntake(null);
    setIsIntakeOpen(true);
  };
  const openBlankBuilder = () => {
    setIsIntakeOpen(false);
    navigate(`/marketing/email/automatyzacje/edytor?brand=${encodeURIComponent(selectedBrandId)}`);
  };
  return (
    <section className="space-y-4" aria-label="Automatyzacje email">
      <div className="flex flex-col gap-3 rounded-card border border-border-subtle bg-surface-raised/50 p-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="w-full sm:w-72">
          <label htmlFor="marketing-automation-brand" className="mb-1.5 block text-xs font-medium text-text-secondary">
            Przestrzeń marki
          </label>
          <Select
            id="marketing-automation-brand"
            value={selectedBrandId}
            onChange={event => {
              setBrandId(event.target.value);
              setIsIntakeOpen(false);
              setEditingIntake(null);
            }}
          >
            {brands.map(brand => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
          </Select>
        </div>
        <Button variant="primary" leftIcon={<Plus />} onClick={openNewIntake}>
          Nowa automatyzacja
        </Button>
      </div>

      {intakesQuery.isError ? (
        <div role="alert" className="rounded-card border border-danger/30 bg-danger/5 p-3 text-sm text-danger">
          Nie udało się pobrać zapisanych briefów. Istniejące flow pozostają dostępne poniżej.
        </div>
      ) : intakes.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-text-primary">Briefy przed generowaniem</h2>
              <p className="mt-0.5 text-xs text-text-muted">Niezmienne wersje intake. Nie uruchamiają AI ani wysyłki.</p>
            </div>
            {intakesQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin text-accent-primary" aria-label="Odświeżanie briefów" /> : null}
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {intakes.map(intake => {
              const latest = intake.versions[0];
              return (
                <button
                  key={intake.id}
                  type="button"
                  onClick={() => {
                    setEditingIntake(intake);
                    setIsIntakeOpen(true);
                  }}
                  className="rounded-card border border-accent-primary/20 bg-accent-primary/5 p-4 text-left transition-colors hover:border-accent-primary/40 hover:bg-accent-primary/10"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-text-primary">{intake.name}</h3>
                      <p className="mt-1 text-xs text-text-muted">
                        {latest?.mode === 'GOAL' ? 'Cel' : 'Blueprint'}: {latest?.selectionKey ?? 'brak'}
                      </p>
                    </div>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-accent-primary/20 px-2 py-1 text-[10px] font-medium text-accent-primary">
                      <ClipboardPenLine className="h-3 w-3" aria-hidden="true" /> v{intake.latestVersionNumber}
                    </span>
                  </div>
                  <p className="mt-3 text-[11px] text-text-muted">Kliknij, aby zapisać nową wersję briefu</p>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {flowsQuery.isLoading ? (
        <div className="flex min-h-40 items-center justify-center text-accent-primary" aria-label="Ładowanie flow">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : flowsQuery.isError ? (
        <EmptyState
          icon={AlertTriangle}
          title="Nie udało się pobrać flow"
          description={describeMarketingLoadError(flowsQuery.error)}
          buttonLabel="Spróbuj ponownie"
          onButtonClick={() => void flowsQuery.refetch()}
        />
      ) : flows.length === 0 ? (
        <EmptyState
          icon={Workflow}
          title="Brak automatyzacji email"
          description={intakes.length > 0
            ? 'Brief jest zapisany. Generowanie flow pozostaje zablokowane do wdrożenia walidowanego pipeline M3-02.'
            : 'Zacznij od celu biznesowego lub blueprintu. Zapis briefu nie uruchomi AI ani wysyłki.'}
          buttonLabel={intakes.length > 0 ? undefined : 'Utwórz brief'}
          onButtonClick={intakes.length > 0 ? undefined : openNewIntake}
          buttonIcon={intakes.length > 0 ? undefined : ClipboardPenLine}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {flows.map(flow => (
            <Link
              key={flow.id}
              to={`/marketing/email/automatyzacje/edytor?brand=${encodeURIComponent(selectedBrandId)}&flow=${encodeURIComponent(flow.id)}`}
              className="rounded-card border border-border-subtle bg-surface-raised/50 p-4 transition-colors hover:border-accent-primary/30 hover:bg-surface-hover/50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-semibold text-text-primary">{flow.name}</h2>
                  <p className="mt-1 text-xs text-text-muted">
                    v{flow.latestVersionNumber}
                    {flow.publishedVersionNumber ? `, opublikowana v${flow.publishedVersionNumber}` : ', bez publikacji'}
                  </p>
                </div>
                <span className="shrink-0 rounded-full border border-border px-2 py-1 text-[10px] font-medium text-text-secondary">
                  {FLOW_STATUS_LABELS[flow.status]}
                </span>
              </div>
              <div className="mt-4 flex items-center justify-between text-[11px] text-text-muted">
                <span>{flow._count.runs} aktywnych runów</span>
                <span>{new Date(flow.updatedAt).toLocaleDateString('pl-PL')}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
      <MarketingSequenceIntakeModal
        isOpen={isIntakeOpen}
        marketingBrandId={selectedBrandId}
        intake={editingIntake}
        onClose={() => {
          setIsIntakeOpen(false);
          setEditingIntake(null);
        }}
        onOpenBlankBuilder={openBlankBuilder}
      />
    </section>
  );
}

function MarketingMessagesPanel() {
  const [brandId, setBrandId] = useState('');
  const brandsQuery = useQuery({
    queryKey: ['marketing-brands'],
    queryFn: async () => (await marketingApi.listBrands()).data,
    staleTime: 60_000,
  });
  const brands = brandsQuery.data ?? [];
  const selectedBrandId = brands.some(brand => brand.id === brandId) ? brandId : brands[0]?.id ?? '';
  const messagesQuery = useQuery({
    queryKey: ['marketing-messages', selectedBrandId],
    queryFn: async () => (await marketingApi.listMessages(selectedBrandId)).data,
    enabled: Boolean(selectedBrandId),
  });

  if (brandsQuery.isLoading) {
    return <div className="flex min-h-48 items-center justify-center text-accent-primary" aria-label="Ładowanie wiadomości"><Loader2 className="h-7 w-7 animate-spin" /></div>;
  }
  if (brandsQuery.isError) {
    return <EmptyState icon={AlertTriangle} title="Nie udało się pobrać przestrzeni marki" description={describeMarketingLoadError(brandsQuery.error)} buttonLabel="Spróbuj ponownie" onButtonClick={() => void brandsQuery.refetch()} />;
  }
  if (!selectedBrandId) {
    return <EmptyState icon={LetterText} title="Brak przestrzeni marki" description="Utwórz sklep lub MarketingBrand, aby rozpocząć komponowanie wiadomości email." />;
  }

  const messages = messagesQuery.data ?? [];
  return (
    <section className="space-y-4" aria-label="Wiadomości email">
      <div className="flex flex-col gap-3 rounded-card border border-border-subtle bg-surface-raised/50 p-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="w-full sm:w-72">
          <label htmlFor="marketing-message-list-brand" className="mb-1.5 block text-xs font-medium text-text-secondary">Przestrzeń marki</label>
          <Select id="marketing-message-list-brand" value={selectedBrandId} onChange={event => setBrandId(event.target.value)}>
            {brands.map(brand => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
          </Select>
        </div>
        <Link to={`/marketing/email/wiadomosci/edytor?brand=${encodeURIComponent(selectedBrandId)}`} className="btn-primary inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Nowa wiadomość
        </Link>
      </div>

      {messagesQuery.isLoading ? (
        <div className="flex min-h-40 items-center justify-center text-accent-primary" aria-label="Ładowanie listy wiadomości"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : messagesQuery.isError ? (
        <EmptyState icon={AlertTriangle} title="Nie udało się pobrać wiadomości" description={describeMarketingLoadError(messagesQuery.error)} buttonLabel="Spróbuj ponownie" onButtonClick={() => void messagesQuery.refetch()} />
      ) : messages.length === 0 ? (
        <EmptyState
          icon={LetterText}
          title="Brak wiadomości email"
          description="Utwórz pierwszy block/plain dokument. Każdy zapis serwerowy powstanie jako osobna, niezmienna wersja."
          buttonLabel="Otwórz kompozytor"
          buttonTo={`/marketing/email/wiadomosci/edytor?brand=${encodeURIComponent(selectedBrandId)}`}
          buttonIcon={LetterText}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {messages.map(message => (
            <Link
              key={message.id}
              to={`/marketing/email/wiadomosci/edytor?brand=${encodeURIComponent(selectedBrandId)}&message=${encodeURIComponent(message.id)}`}
              className="rounded-card border border-border-subtle bg-surface-raised/50 p-4 transition-colors hover:border-accent-primary/30 hover:bg-surface-hover/50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-semibold text-text-primary">{message.name}</h2>
                  <p className="mt-1 text-xs text-text-muted">Immutable v{message.latestVersionNumber}</p>
                </div>
                <span className="shrink-0 rounded-full border border-border px-2 py-1 text-[10px] font-medium text-text-secondary">{message.status === 'DRAFT' ? 'Szkic' : 'Archiwum'}</span>
              </div>
              <div className="mt-4 flex items-center justify-between text-[11px] text-text-muted">
                <span>JSON schema v1</span>
                <span>{new Date(message.updatedAt).toLocaleDateString('pl-PL')}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

export function MarketingEmailPage({ view }: { view: MarketingEmailView }) {
  const user = useAuthStore(state => state.user);
  const canManageSettings = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const config = viewConfig[view];

  if (view === 'settings' && !canManageSettings) {
    return (
      <div>
        <EmailNavigation showSettings={false} />
        <PageHeader
          icon={Shield}
          title="Ustawienia Email"
          subtitle="Dostęp do ustawień providera, zgód i limitów jest ograniczony."
        />
        <EmptyState
          icon={Shield}
          title="Brak uprawnień"
          description="Ustawienia Email może zmieniać administrator lub właściciel konta."
        />
      </div>
    );
  }

  return (
    <div>
      <EmailNavigation showSettings={canManageSettings} />
      <PageHeader icon={config.icon} title={config.title} subtitle={config.subtitle} />
      {view === 'messages' ? (
        <MarketingMessagesPanel />
      ) : view === 'automations' ? (
        <MarketingAutomationsPanel />
      ) : view === 'recipients' ? (
        <MarketingAudiencePanel />
      ) : view === 'analytics' ? (
        <MarketingContributionPanel />
      ) : view === 'sends' ? (
        <MarketingBroadcastPanel />
      ) : (
        <EmptyState
          icon={config.icon}
          title={config.emptyTitle}
          description={config.emptyDescription}
        />
      )}
    </div>
  );
}
